// =============================================================================
// FILE: packages/renderer/src/stores/founderMode.store.ts
// PURPOSE: R2 Founder Mode — per-device/session unlock of the Founder Edition.
//   The passcode is verified server-side (never in frontend code); on success a
//   short-lived signed JWT is stored locally and re-validated on boot. Secrets
//   live in the backend .env and can be rotated without touching this code.
// =============================================================================

import { create } from 'zustand';

const LS_TOKEN = 'rcd2.founder.token';

export function getFounderToken(): string | null {
  try { return localStorage.getItem(LS_TOKEN); } catch { return null; }
}

export function hasFounderToken(): boolean {
  return Boolean(getFounderToken());
}

export function applyFounderClass(active: boolean): void {
  try { document.documentElement.classList.toggle('founder-edition', active); } catch { /* */ }
}

interface FounderState {
  active: boolean;
  token: string | null;
  verifying: boolean;
  error: string | null;
  verify: (passcode: string) => Promise<boolean>;
  exit: () => void;
  hydrate: () => Promise<void>;
}

export const useFounderMode = create<FounderState>((set) => ({
  active: false,
  token: null,
  verifying: false,
  error: null,

  verify: async (passcode: string) => {
    set({ verifying: true, error: null });
    try {
      const r = await fetch('/api/founder/verify', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ passcode }),
      });
      const d = (await r.json()) as { token?: string; detail?: string };
      if (!r.ok || !d.token) {
        set({ verifying: false, error: typeof d.detail === 'string' ? d.detail : 'Verification failed.' });
        return false;
      }
      try { localStorage.setItem(LS_TOKEN, d.token); } catch { /* */ }
      applyFounderClass(true);
      set({ active: true, token: d.token, verifying: false, error: null });
      try { window.dispatchEvent(new CustomEvent('r2:founder-mode-changed', { detail: { active: true } })); } catch { /* */ }
      return true;
    } catch {
      set({ verifying: false, error: 'Could not reach the verification service.' });
      return false;
    }
  },

  exit: () => {
    try { localStorage.removeItem(LS_TOKEN); } catch { /* */ }
    applyFounderClass(false);
    set({ active: false, token: null, error: null });
    try { window.dispatchEvent(new CustomEvent('r2:founder-mode-changed', { detail: { active: false } })); } catch { /* */ }
  },

  hydrate: async () => {
    let token: string | null = null;
    try { token = localStorage.getItem(LS_TOKEN); } catch { /* */ }
    if (!token) return;
    applyFounderClass(true);
    set({ active: true, token });
    try {
      const r = await fetch('/api/founder/status', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });
      const d = (await r.json()) as { founder?: boolean };
      if (d.founder) { applyFounderClass(true); set({ active: true, token }); }
      else { try { localStorage.removeItem(LS_TOKEN); } catch { /* */ } applyFounderClass(false); set({ active: false, token: null }); }
    } catch { /* offline — leave inactive, non-blocking */ }
  },
}));
